<script setup lang="ts">
import { LoginPreCheck } from '@/api/login'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { type CheckboxValueType, type FormInstance, type FormRules } from 'element-plus'
import { computed, inject, reactive, ref, watch } from 'vue'
import { LoginPwdSingle } from '../../api/login'
import SelectAccount from '../SelectAccount.vue'
import Captcha from '../Captcha/index.vue'
import { IconFont, PassControl } from '@mp/component'
import { captureEvent, captureMessage } from '@sentry/vue'
import { useIsSp } from '@mp/hooks'

const { isNotSp } = useIsSp()
declare global {
  interface Window {
    pgeFocus: () => void
    pgeBlur: () => void
  }
}

const toggle = inject('toggle', () => { })
const isPassControl = ref<boolean>(false)

const captchaRef = ref<InstanceType<typeof Captcha> | null>(null)
const selectorRef = ref<InstanceType<typeof SelectAccount> | null>(null)

const passControlRef = ref<InstanceType<typeof PassControl> | null>(null)

const formRef = ref<FormInstance>()
const formMessage = reactive<{
  message: string | undefined
  type: 'default' | 'error' | 'success'
}>({
  message: undefined,
  type: 'error',
})

const rules = reactive<FormRules>({
  loginName: [{ required: true, message: '请输入邮箱/手机号', trigger: 'blur' }],
  loginPwd: [{ required: true, message: '请输入密码', trigger: 'blur' }],
})
const formValues = reactive<{
  loginName: string | undefined
  loginPwd: string | undefined
  certSerialNo?: string // 证书序列号
}>({
  loginName: undefined,
  loginPwd: undefined,
  certSerialNo: undefined,
})

// #预校验，用来判断是否需要行为验证码等
const { data: preCheckData, refetch: refetchPreCheckData, isError: isPreCheckDataError } = useQuery({
  queryKey: ['login:check:account', isPassControl],
  queryFn: () => LoginPreCheck({
    needPwdWidget: isPassControl.value,
  }),
  retry: false,
  refetchOnWindowFocus: false,
})

// ?如果该接口异常， 大概率是公钥异常
watch(isPreCheckDataError, () => {
  if (isPreCheckDataError.value) {
    sessionStorage.removeItem('publicKeyRes')
    refetchPreCheckData()
  }
})

// ?密码控件需要的参数
const pwdWidgetParams = computed(() => {
  try {
    if (preCheckData.value?.pwdWidget)
      return JSON.parse(preCheckData.value.pwdWidget)
    return null
  }
  catch (error) {
    console.error('解析 pwdWidget 失败:', error)
    return null
  }
})
// #单账号登录
const { mutateAsync, isLoading } = useMutation({
  mutationFn: LoginPwdSingle,
  onSuccess(data) {
    if (!data.loginAwaitSelectors)
      return

    captureEvent({
      level: 'log',
      event_id: 'click_login',
      message: '点击登录',
      extra: {
        type: isPassControl.value ? '使用密码控件登录' : '使用密码登录',
      },
    })
    captureMessage(isPassControl.value ? '使用密码控件登录' : '使用密码登录')

    // ?如果是单账号
    if (data?.loginAwaitSelectors?.length === 1) {
      selectorRef.value?.handleChecked(data?.loginAwaitSelectors[0], data.loginTicket)
      return
    }

    selectorRef.value?.open({
      data: data.loginAwaitSelectors,
      loginTicket: data.loginTicket,
    })

    refetchPreCheckData()
  },

  onError(e: any) {
    const { resultCode, errorCode, errorCodeDes } = e
    if (errorCodeDes)
      formMessage.message = errorCodeDes
    if (resultCode === 'FAIL' && errorCode === 'CAPTCHA_VALID_FAIL') {
      refetchPreCheckData()
      return
    }

    refetchPreCheckData()
    if (errorCodeDes) {
      formMessage.message = errorCodeDes
      formMessage.type = 'error'
    }
  },
})

// ?行为验证成功后
async function handleCaptchOnSuccess(value: string) {
  const params = {
    loginName: formValues.loginName as string,
    tempToken: preCheckData.value?.tempToken as string,
    thirdCaptchaId: value,
    certSerialNo: formValues.certSerialNo, // 证书序列号
    randomKey: pwdWidgetParams.value?.randomKey, // 随机数
  } as any

  if (isPassControl.value)
    params.ciphertext = formValues.loginPwd
  else
    params.loginPwd = formValues.loginPwd

  mutateAsync(params).catch(() => undefined)
}

function handleSubmit() {
  // 如果开启了密码控件，则给表单赋值
  if (isPassControl.value) {
    const password = passControlRef.value?.getPassValue()
    formValues.loginPwd = password
    formValues.certSerialNo = pwdWidgetParams.value?.certSerialNo
    passControlRef.value?.clear()
  }

  formRef.value?.validate((valid: boolean, fields: any) => {
    if (valid) {
      formMessage.message = undefined

      // ?需要行为验证
      if (preCheckData.value?.needCaptcha) {
        captchaRef.value?.open()
        return
      }

      const params = {
        loginName: formValues.loginName as string,
        tempToken: preCheckData.value?.tempToken as string,
        certSerialNo: formValues.certSerialNo, // 证书序列号
        randomKey: pwdWidgetParams.value?.randomKey, // 随机数
      } as any

      if (isPassControl.value)
        params.ciphertext = formValues.loginPwd
      else
        params.loginPwd = formValues.loginPwd

      mutateAsync(params).catch(() => undefined)
    }
    else {
      const lastError = [].concat(...Object.keys(fields).map(key => fields[key]))?.[0] as any
      if (lastError)
        formMessage.message = lastError.message
      formMessage.type = 'error'
    }
  })
}

const FORGOT_URL = isNotSp ? `${window.location.origin}/merchant-pw/forget` : `${import.meta.env.VITE_SASS_URL}/merchant-pw/forget`

function handleCheckboxChange(value: CheckboxValueType) {
  if (value === false)
    formValues.loginPwd = undefined
}

window.pgeFocus = function () {
}

window.pgeBlur = function () {
}
</script>

<template>
  <h3 class="text-sm mb-[28px] mt-0 font-bold leading-none">
    账号密码登录
  </h3>
  <el-form ref="formRef" :rules="rules" :model="formValues" size="large" :show-message="false">
    <div class="h-[120px]">
      <el-form-item prop="loginName" style="margin-bottom: 10px;">
        <el-input v-model="formValues.loginName" placeholder="请输入商户登录账号/操作员登录账号" max-length="11">
          <template #prefix>
            <div class="icon-container">
              <IconFont type="icon-user" class="text-[#ccc] mr-2 text-4" />
            </div>
          </template>
        </el-input>
      </el-form-item>

      <el-form-item prop="loginPwd" style="margin-bottom: 10px;">
        <template v-if="isPassControl && pwdWidgetParams">
          <PassControl
            ref="passControlRef"
            async
            :affine-x="pwdWidgetParams?.affineX"
            :affine-y="pwdWidgetParams?.affineY"
            :pge-r-z-rand-num="pwdWidgetParams?.sKey"
            :pge-r-z-data-b="pwdWidgetParams?.enStr"
            :random-key="pwdWidgetParams?.randomKey ?? ''"
            input-id="login_pass_input"
            input-container-id="login_pass_input_containersss"
          />
          <p v-if="!isNotSp" class="text-xs text-center mt-2 w-full text-error">
            安全控件安装后仍提示未安装？<a href="/merchant-platform/盛意旺SaaS平台使用问题Q&A/index.html" class="text-primary" target="_blank">点击这里查看解决方法</a>
          </p>
        </template>

        <el-input v-else v-model="formValues.loginPwd" type="password" placeholder="请输入密码" max-length="11" @keyup.enter="handleSubmit">
          <template #prefix>
            <div class="icon-container">
              <IconFont type="icon-lock" class="text-[#ccc] mr-2 text-4" />
            </div>
          </template>
        </el-input>
      </el-form-item>

      <div v-if="formMessage.message" class="flex items-center text-xs mt-2 mb-1 text-[var(--el-color-danger)]">
        <IconFont type="icon-exclamation-circle" class="text-base leading-none mr-1 -mt-[1px]" /> {{ formMessage.message
        }}
      </div>
    </div>

    <el-checkbox
      v-model="isPassControl"
      style="--el-checkbox-checked-text-color: white; --el-checkbox-bg-color: transparent; --el-checkbox-text-color: rgba(255,255,255,0.8)"
      @change="handleCheckboxChange"
    >
      <span class="text-xs font-normal">使用密码控件登录
        <template v-if="isPassControl">
          (若安装失败或无法登录请取消勾选)
        </template>
      </span>
    </el-checkbox>

    <el-button :loading="isLoading" type="primary" class="w-full" size="large" @click="handleSubmit">
      登录
    </el-button>
  </el-form>

  <div class=" flex justify-between mt-2 leading-none" style="--el-color-primary: #fff">
    <el-button
      v-track
      tag="a" target="_blank" link type="primary" size="small" class="hover:underline text-#fff"
      :href="FORGOT_URL"
    >
      忘记密码?
    </el-button>
    <p v-if="!isNotSp" v-track class="hover:text-primary cursor-pointer flex items-center text-xs text-[#fff]" @click="toggle()">
      <IconFont type="icon-phone" class=" text-lg mr-1 leading-none -mt-[2px]" /> 手机验证码登录
    </p>
  </div>

  <Captcha ref="captchaRef" @success="handleCaptchOnSuccess" />

  <!-- 账户选择 -->
  <SelectAccount ref="selectorRef" type="account" />
</template>

<style scoped>
.icon-container {
  @apply b-r b-r-solid b-r-[#eee] h-[20px] -ml-1 leading-none;
}
</style>
